import { useEffect, useState } from "react";
import { accApi, accErr, money } from "./AccountsPanel";

const Card = ({ label, value, tone = "text-slate-900", testid }) => (
  <div className="rounded-xl border bg-white p-4" data-testid={testid}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`mt-1 text-xl font-bold ${tone}`}>{value}</div>
  </div>
);

const List = ({ title, rows, testid }) => (
  <div className="rounded-xl border bg-white p-4" data-testid={testid}>
    <div className="mb-2 text-sm font-semibold text-slate-700">{title}</div>
    {rows.length ? rows.slice(0, 12).map(r => (
      <div key={r.party_id} className="flex justify-between border-b border-slate-100 py-1 text-sm last:border-0">
        <span className="truncate">{r.name}</span><b>{money(r.balance)}</b></div>)) : <p className="text-xs text-slate-400">None</p>}
  </div>
);

export const AccDashboard = ({ company }) => {
  const [d, setD] = useState(null);
  const [exp, setExp] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => { accApi(`/stock/expiring?company_id=${company.id}&days=90`).then(setExp).catch(() => {}); }, [company.id]);
  useEffect(() => {
    accApi(`/reports/summary?company_id=${company.id}&date_from=${from}&date_to=${to}`).then(setD).catch(accErr);
  }, [company.id, from, to]);
  if (!d) return <p className="text-sm text-slate-500">Loading…</p>;
  const bs = d.balance_sheet;
  return (
    <div className="space-y-4" data-testid="acc-dashboard">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        Period <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="rounded border px-2 py-1" data-testid="acc-dash-from" />
        to <input type="date" value={to} onChange={e => setTo(e.target.value)} className="rounded border px-2 py-1" data-testid="acc-dash-to" />
        <span className="text-slate-400">(blank = all time)</span>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card label="Sales (taxable, ₹)" value={money(d.sales)} testid="acc-card-sales" />
        <Card label="Purchase (taxable, ₹)" value={money(d.purchase)} testid="acc-card-purchase" />
        <Card label="Gross profit (FIFO cost)" value={money(d.gross_profit)} tone={d.gross_profit >= 0 ? "text-emerald-700" : "text-rose-700"} testid="acc-card-profit" />
        <Card label="Closing stock value" value={money(d.stock_value)} testid="acc-card-stock" />
        <Card label="Sundry debtors (to receive)" value={money(d.debtors_total)} tone="text-indigo-700" testid="acc-card-debtors" />
        <Card label="Sundry creditors (to pay)" value={money(d.creditors_total)} tone="text-amber-700" testid="acc-card-creditors" />
        <Card label="GST outward (tax)" value={money(d.gst_outward.igst + d.gst_outward.cgst + d.gst_outward.sgst)} testid="acc-card-gst-out" />
        <Card label="GST payable (out − in)" value={money(d.gst_payable)} tone={d.gst_payable > 0 ? "text-rose-700" : "text-emerald-700"} testid="acc-card-gst-pay" />
        {exp && <Card label={`Batches expiring ≤ 90 days${exp.expired ? ` · ${exp.expired} expired` : ""}`} value={`${exp.count} · ${money(exp.value)}`}
          tone={exp.count ? "text-amber-700" : "text-emerald-700"} testid="acc-card-expiring" />}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border bg-white p-4" data-testid="acc-pl">
          <div className="mb-2 text-sm font-semibold">Profit &amp; Loss</div>
          {[["Sales", d.sales], ["Less: cost of goods sold", -d.cogs], ["Gross profit", d.gross_profit],
            ...(d.expense_heads || []).map(x => [`Less: ${x.name}`, -x.balance]), ...(d.income_heads || []).map(x => [`Add: ${x.name}`, -x.balance]), ["Net profit", d.net_profit]].map(([l, v]) => (
            <div key={l} className={`flex justify-between py-1 text-sm ${l === "Net profit" ? "border-t font-bold" : ""}`}><span>{l}</span><b data-testid={l === "Net profit" ? "acc-net-profit" : undefined}>{money(v)}</b></div>))}
        </div>
        <div className="rounded-xl border bg-white p-4" data-testid="acc-bs">
          <div className="mb-2 text-sm font-semibold">Balance sheet</div>
          <div className="text-[11px] font-semibold uppercase text-slate-400">Assets</div>
          {Object.entries(bs.assets).map(([l, v]) => <div key={l} className="flex justify-between py-0.5 text-sm"><span>{l}</span><b>{money(v)}</b></div>)}
          <div className="flex justify-between border-t py-1 text-sm font-bold"><span>Total assets</span><span data-testid="acc-bs-assets">{money(bs.assets_total)}</span></div>
          <div className="mt-2 text-[11px] font-semibold uppercase text-slate-400">Liabilities &amp; capital</div>
          {Object.entries(bs.liabilities).map(([l, v]) => <div key={l} className="flex justify-between py-0.5 text-sm text-slate-600"><span>{l}</span><b>{money(v)}</b></div>)}
          <div className="flex justify-between border-t py-1 text-sm font-bold"><span>Total liabilities</span><span data-testid="acc-bs-liab">{money(bs.liabilities_total)}</span></div>
        </div>
        <List title="Top debtors" rows={d.debtors} testid="acc-debtors" />
      </div>
    </div>
  );
};
